import { GrundartikelLink } from '@/components/catalog/GrundartikelLink'
import { AuftragAnlegenDialog } from './AuftragAnlegenDialog'
import { AuftragSymbol } from './AuftragSymbol'
import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/api/auth'
import { api, apiFetch, ApiError } from '@/api/client'
import {
  useCurrentPallet,
  usePalletOverview,
  palletApiPath,
  type PalletOverview,
  type PalletTransportRecord,
} from '@/api/warehouse'
import type { WarehouseCompanyFilter } from '@/lib/warehouse-company'
import { palletRequestBody, palletSourceText, palletTargetLager } from '@/lib/fillup-anforderung'
import { neueUuid } from '@/lib/uuid'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { palletPlaceConfirmation, palletReplaceBody, palletReplaceHeaders, type PalletPlaceConfirmation } from '@/lib/pallet-place'
type Intent = { path: string; body: Record<string, unknown>; operation: string }
type Waiting = PalletOverview['vormerkungen'][number]
// Plan 2026-10-23 S1: list-level storno asks once before any write.
type ListConfirm = { kind: 'cancel'; row: PalletTransportRecord } | { kind: 'withdraw'; row: Waiting }
/** Readable refusal for list storno/withdraw; definite server answers, nothing was written. */
function refusalText(e: unknown) {
  const code = e instanceof ApiError ? String(e.details.code ?? e.code) : undefined
  if (code === 'pallet_owner_required') return 'Keine Berechtigung für diesen Palettenauftrag.'
  if (code === 'pallet_version_conflict') return 'Auftrag hat sich inzwischen geändert — Liste neu geladen.'
  if (code === 'pallet_withdraw_not_pending') return 'Auftrag wartet nicht mehr (schon Palette zugeteilt oder storniert) — Liste neu geladen.'
  return null
}
export function PalletTransport({
  company,
  enabled,
  lager: lagerProp,
  onLagerChange,
}: {
  company: WarehouseCompanyFilter
  enabled: boolean
  lager?: 1 | 2
  onLagerChange?: (lager: 1 | 2) => void
}) {
  const [localLager, setLocalLager] = useState<1 | 2>(2)
  const lager = lagerProp ?? localLager
  const setLager = onLagerChange ?? setLocalLager
  const { user, warehouse } = useAuth(),
    qc = useQueryClient(),
    overview = usePalletOverview(company, lager),
    current = useCurrentPallet(company)
  const identity = JSON.stringify([user?.id, warehouse, company]),
    activeIdentity = useRef(identity)
  activeIdentity.current = identity
  const storageKey = `stahlia_pallet_intent:${user?.id}:${company}`
  const [selected, setSelected] = useState<PalletTransportRecord | null>(null),
    [qr, setQr] = useState(''),
    [code, setCode] = useState(''),
    [receiver, setReceiver] = useState(''),
    [receiverDevice, setReceiverDevice] = useState(''),
    [receiverWorker, setReceiverWorker] = useState(''),
    [returning, setReturning] = useState(false),
    [error, setError] = useState(''),
    [message, setMessage] = useState(''),
    [intent, setIntent] = useState<Intent | null>(null),
    [busy, setBusy] = useState(false)
  const busyRef = useRef(false)
  const [replacement, setReplacement] = useState<{ confirmation: PalletPlaceConfirmation; book: Intent } | null>(null)
  const [listConfirm, setListConfirm] = useState<ListConfirm | null>(null)
  const [transferOpen, setTransferOpen] = useState(false)
  const [transferTarget, setTransferTarget] = useState<PalletTransportRecord | null>(null)
  const [transferKind, setTransferKind] = useState<'user' | 'scanner'>('user')
  const currentTransport = useRef<number | null>(null)
  const enteredList = useRef<string | null>(null)
  useEffect(() => {
    setSelected(null)
    setTransferOpen(false)
    setTransferTarget(null)
    setReplacement(null)
    setListConfirm(null)
    setQr('')
    setError('')
    setMessage('')
    try {
      setIntent(JSON.parse(localStorage.getItem(storageKey) ?? 'null') as Intent | null)
    } catch {
      setError('Gespeicherte Buchung unklar — Lagerleiter informieren')
    }
  }, [storageKey, identity])
  useEffect(() => {
    if (!enabled || intent || !user?.id || !(warehouse.count || warehouse.lead)) return
    try {
      if (localStorage.getItem(storageKey)) return
    } catch {
      return // The saved-intent loader reports unavailable storage; do not retry writes.
    }
    if (enteredList.current === `${identity}:${lager}`) return
    enteredList.current = `${identity}:${lager}`
    const origin = identity
    // Enter once through the write-bearing list endpoint. Overview polling stays read-only.
    void apiFetch(
      palletApiPath(`/warehouse/fillup/list/?zone=fuss&lager=${lager}&request_kind=pallet`, company),
    )
      .then(() => {
        if (activeIdentity.current === origin)
          void qc.invalidateQueries({ queryKey: ['warehouse', 'pallet', 'overview'] })
      })
      .catch((e: unknown) => {
        if (activeIdentity.current === origin)
          setError(e instanceof Error ? e.message : 'Palettenliste konnte nicht geöffnet werden.')
      })
  }, [
    identity,
    lager,
    enabled,
    intent,
    user?.id,
    warehouse.count,
    warehouse.lead,
    company,
    storageKey,
    qc,
  ])
  useEffect(() => {
    if (transferOpen) return
    if (!current.data) return
    if (current.data.data) {
      currentTransport.current = current.data.data.transport_id
      setSelected(current.data.data)
    } else if (currentTransport.current !== null) {
      const previous = currentTransport.current
      currentTransport.current = null
      setSelected(row => row?.transport_id === previous ? null : row)
    }
  }, [current.data, transferOpen])
  // A list storno targets a row, not necessarily the opened pallet: close only that pallet.
  // Without a transport_id in the answer the previous behaviour (close) stays.
  const clearSelected = (transportId: number | undefined) =>
    setSelected((s) => (s && transportId != null && s.transport_id !== transportId ? s : null))
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['warehouse', 'pallet'] })
  }
  // readyCancel: only the storno of a ready pallet (S1) gets readable refusals and drops its
  // intent on 403; physical steps and recover() keep the previous behaviour.
  function failure(e: unknown, book: Intent, readyCancel = false) {
    // Warehouse envelopes use a numeric error[0].code and the business code at top level.
    const code = e instanceof ApiError ? String(e.details.code ?? e.code) : undefined
    const c = e instanceof ApiError && !e.undecodable && e.status === 409
      ? palletPlaceConfirmation(code, e.details) : null
    setReplacement(book.operation === 'place' && c ? { confirmation: c, book } : null)
    const refusal = readyCancel ? refusalText(e) : null
    setError(c ? '' : refusal ?? (code === 'place_occupant_changed'
      ? 'Platz hat sich geändert — bitte erneut scannen'
      : code === 'company_forbidden'
        ? 'Palette einer anderen Firma kann hier nicht ersetzt werden.'
        : book.body.replace && e instanceof TypeError
          ? 'Netz erforderlich — Ergebnis unbekannt. Zuerst Ergebnis prüfen; nichts in die Warteschlange gestellt.'
          : e instanceof Error ? e.message : 'Ergebnis unbekannt'))
    if (e instanceof ApiError && !e.undecodable && ([400, 409, 422].includes(e.status) || code === 'company_forbidden' || (readyCancel && code === 'pallet_owner_required'))) {
      localStorage.removeItem(storageKey)
      setIntent(null)
    }
  }
  function onlineAnswer() {
    if (navigator.onLine === false) {
      setError('Netz erforderlich — Palettenersatz nur online. Nichts in die Warteschlange gestellt.')
      return false
    }
    return true
  }
  function declineReplace() {
    if (busy || !onlineAnswer()) return
    setReplacement(null)
    setQr('')
    setError('')
  }
  async function confirmReplace() {
    if (!replacement || busyRef.current || intent || !onlineAnswer()) return
    const book = { ...replacement.book, body: palletReplaceBody(replacement.book.body, replacement.confirmation) }
    const origin = identity
    busyRef.current = true
    setBusy(true)
    setError('')
    try {
      localStorage.setItem(storageKey, JSON.stringify(book))
      setIntent(book)
      setReplacement(null)
      await dispatch(book, origin)
    } catch (e) {
      if (activeIdentity.current === origin) failure(e, book)
    } finally {
      busyRef.current = false
      setBusy(false)
    }
  }
  async function dispatch(book: Intent, origin: string) {
    const result = await api.post<Omit<PalletTransportRecord, 'status'> & { status: string }>(
      book.path,
      book.body,
      palletReplaceHeaders,
    )
    if (activeIdentity.current !== origin) throw new Error('Anmeldung geändert — Ergebnis prüfen')
    if (
      (result as unknown as { queued?: boolean; blocked?: boolean }).queued ||
      (result as unknown as { blocked?: boolean }).blocked ||
      (result as unknown as { ack?: number }).ack === 0
    )
      throw new Error('Ergebnis nicht bestätigt')
    const valid =
      book.operation === 'request'
        ? ['angefordert', 'vorgemerkt']
        : book.operation === 'place'
          ? ['placed', 'cancelled']
          : book.operation === 'cancel'
            ? ['cancelled', 'in_transit']
            : ['in_transit']
    if (!valid.includes(result.status)) throw new Error('Ergebnis nicht bestätigt')
    localStorage.removeItem(storageKey)
    setIntent(null)
    if (book.operation === 'request') {
      setMessage(
        result.status === 'vorgemerkt'
          ? 'Vorgemerkt — wartet auf Palette'
          : 'Palettenauftrag angelegt',
      )
      setCode('')
    } else if (result.status === 'placed' || result.status === 'cancelled') {
      clearSelected(result.transport_id)
      setMessage(
        result.status === 'placed'
          ? 'Palette abgestellt'
          : 'Palette zurückgestellt / Auftrag storniert',
      )
    } else {
      setSelected(result as PalletTransportRecord)
      if (book.operation === 'cancel') setReturning(true)
    }
    setQr('')
    refresh()
  }
  async function run(operation: string, extra: Record<string, unknown> = {}, target = selected) {
    if (busyRef.current || intent || replacement || !user) return
    if (operation === 'place' && !onlineAnswer()) return
    busyRef.current = true
    setBusy(true)
    setError('')
    setMessage('')
    const origin = identity
    const book: Intent =
      operation === 'request'
        ? {
            operation,
            path: palletApiPath('/warehouse/fillup/request/', company),
            body: palletRequestBody(
              String(extra.code ?? code).trim(),
              neueUuid(),
              extra.roll_length_m as number | undefined,
            ),
          }
        : {
            operation,
            path: palletApiPath(
              `/warehouse/fillup/pallet/${target!.transport_id}/${operation}/`,
              company,
            ),
            body: {
              idempotency_key: neueUuid(),
              version: target!.version,
              generation: target!.generation,
              ...extra,
            },
          }
    try {
      localStorage.setItem(storageKey, JSON.stringify(book))
      setIntent(book)
      await dispatch(book, origin)
      return true
    } catch (e) {
      if (activeIdentity.current === origin) {
        failure(e, book, operation === 'cancel' && target?.status === 'ready')
      }
      return false
    } finally {
      busyRef.current = false
      setBusy(false)
    }
  }
  async function recover() {
    if (!intent || busyRef.current) return
    busyRef.current = true
    setBusy(true)
    setError('')
    const origin = identity
    try {
      const key = String(intent.body.idempotency_key ?? intent.body.request_id)
      let result: PalletTransportRecord | undefined
      try {
        result = await apiFetch<PalletTransportRecord>(
          palletApiPath(
            `/warehouse/fillup/pallet/result/${key}/${intent.operation === 'request' ? '?operation=request' : ''}`,
            company,
          ),
          { headers: palletReplaceHeaders },
        )
      } catch (e) {
        if (!(e instanceof ApiError) || e.status !== 404) throw e
      }
      if (activeIdentity.current !== origin) throw new Error('Anmeldung geändert')
      if (result) {
        const valid =
          intent.operation === 'request'
            ? ['angefordert', 'vorgemerkt']
            : intent.operation === 'place'
              ? ['placed', 'cancelled']
              : intent.operation === 'cancel'
                ? ['cancelled', 'in_transit']
                : ['in_transit']
        const ack = result as unknown as { queued?: boolean; blocked?: boolean; ack?: number }
        if (ack.queued || ack.blocked || ack.ack === 0 || !valid.includes(result.status))
          throw new Error('Ergebnis nicht bestätigt')
        localStorage.removeItem(storageKey)
        setIntent(null)
        if (result.status === 'in_transit') setSelected(result)
        else clearSelected(result.transport_id)
        refresh()
      } else {
        if (intent.operation !== 'request') await current.refetch()
        if (activeIdentity.current !== origin) throw new Error('Anmeldung geändert')
        await dispatch(intent, origin)
      }
    } catch (e) {
      if (activeIdentity.current === origin) failure(e, intent)
    } finally {
      busyRef.current = false
      setBusy(false)
    }
  }
  async function confirmList() {
    const job = listConfirm
    if (!job || busyRef.current) return
    setListConfirm(null)
    if (job.kind === 'cancel') {
      // Same persisted intent / dispatch / failure path as run('cancel') on the opened pallet.
      // dispatch/recover clear `selected` only for this row; an opened other pallet stays open.
      if (await run('cancel', {}, job.row)) {
        setMessage('Palettenauftrag storniert — Palette bleibt am Platz')
      } else refresh()
      return
    }
    // Withdraw has no result endpoint; a repeat is idempotent over request_id (no stored intent).
    busyRef.current = true
    setBusy(true)
    setError('')
    setMessage('')
    const origin = identity
    try {
      const result = await api.post<{ status?: string }>(
        palletApiPath(`/warehouse/fillup/pallet/request/${job.row.id}/withdraw/`, company),
        { request_id: neueUuid() },
      )
      if (activeIdentity.current !== origin) return
      if (result?.status !== 'storniert') throw new Error('Ergebnis nicht bestätigt')
      setMessage('Wartender Auftrag zurückgezogen')
    } catch (e) {
      if (activeIdentity.current === origin)
        setError(refusalText(e) ?? (e instanceof Error ? e.message : 'Zurückziehen fehlgeschlagen'))
    } finally {
      busyRef.current = false
      setBusy(false)
      refresh()
    }
  }
  const rows = [
      ...(overview.data?.lists?.fuss ?? []),
      ...(overview.data?.lists?.stapler ?? []),
    ].map((row) =>
      current.data?.data?.transport_id === row.transport_id
        ? { ...row, ...current.data.data }
        : row,
    ),
    write = warehouse.count || warehouse.lead
  const ownsPhysical = selected?.status === 'ready' || selected?.owner_key === `u:${user?.id}`
  const transferAvailable = !!transferTarget && !overview.isError && !current.isError && !overview.isLoading && !current.isLoading &&
    (rows.some(row => row.transport_id === transferTarget.transport_id && row.status === 'in_transit') ||
      (current.data?.data?.transport_id === transferTarget.transport_id && current.data.data.status === 'in_transit'))
  return (
    <div className="space-y-4">
      <Dialog open={!!replacement} onOpenChange={(open) => {
        if (!open) declineReplace()
      }}>
        <DialogContent onEscapeKeyDown={e => { if (busy) e.preventDefault() }} onPointerDownOutside={e => e.preventDefault()}>
          <DialogTitle>Platz belegt</DialogTitle>
          <DialogDescription className="text-base text-ink-2">{replacement && `Platz ${replacement.confirmation.place.barcode}: Die bisherige Palette wird ausgelagert.`}</DialogDescription>
          {replacement && <div className="grid gap-2 sm:grid-cols-2 text-sm">
            <div className="rounded border border-line p-2"><b>Bisher am Platz</b><p>{replacement.confirmation.occupant.company === 'priostahl' ? 'Priostahl' : replacement.confirmation.occupant.company === 'stahlia' ? 'Stahlia' : '—'} · Palette {replacement.confirmation.occupant.palette_no ?? '—'} · {replacement.confirmation.occupant.article ?? '—'} · Menge {replacement.confirmation.occupant.qty_exact ?? (replacement.confirmation.occupant.qty_circa === null ? 'unbekannt' : `ca. ${replacement.confirmation.occupant.qty_circa}`)}</p></div>
            <div className="rounded border border-line p-2"><b>Neu abstellen</b><p>{replacement.confirmation.incoming?.company === 'priostahl' ? 'Priostahl' : replacement.confirmation.incoming?.company === 'stahlia' ? 'Stahlia' : '—'} · Palette {replacement.confirmation.incoming?.palette_no ?? selected?.palette_no ?? '—'} · {replacement.confirmation.incoming?.article ?? selected?.merchant_sku ?? '—'} · Menge {replacement.confirmation.incoming?.quantity ?? selected?.quantity ?? 'unbekannt'}</p></div>
          </div>}
          {replacement && error && <p role="alert" className="text-red-600">{error}</p>}
          <div className="grid grid-cols-2 gap-3">
            <Button className="min-h-[50px] text-base" disabled={busy} onClick={() => void confirmReplace()}>Ersetzen und auslagern</Button>
            <Button className="min-h-[50px] text-base" variant="outline" disabled={busy} onClick={declineReplace}>Abbrechen</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!listConfirm} onOpenChange={(open) => { if (!open) setListConfirm(null) }}>
        <DialogContent onPointerDownOutside={e => e.preventDefault()}>
          <DialogTitle>{listConfirm?.kind === 'withdraw' ? 'Wartenden Auftrag zurückziehen?' : 'Palettenauftrag stornieren?'}</DialogTitle>
          <DialogDescription className="text-base text-ink-2">
            {listConfirm?.kind === 'cancel' &&
              `${listConfirm.row.merchant_sku} · ${listConfirm.row.palette_no} · Quelle ${listConfirm.row.place_barcode ?? '—'}. Die Palette bleibt am Platz.`}
            {listConfirm?.kind === 'withdraw' &&
              `${listConfirm.row.merchant_sku}${listConfirm.row.roll_length_m ? ` R${listConfirm.row.roll_length_m}` : ''} · 1 ganze Palette. Der Auftrag wird nicht mehr zugeteilt.`}
          </DialogDescription>
          <div className="grid grid-cols-2 gap-3">
            <Button className="min-h-[50px] text-base" disabled={busy} onClick={() => void confirmList()}>{listConfirm?.kind === 'withdraw' ? 'Anforderung zurückziehen' : 'Auftrag stornieren'}</Button>
            <Button className="min-h-[50px] text-base" variant="outline" disabled={busy} onClick={() => setListConfirm(null)}>Abbrechen</Button>
          </div>
        </DialogContent>
      </Dialog>
      {overview.data && (rows.length > 0 || overview.data.vormerkungen.length > 0) && <div className="rounded-lg border border-line bg-panel p-3">
        <b>Lager {lager}</b>
        <p>
          Offen: {overview.data?.progress?.offen ?? '—'} · Wartend:{' '}
          {overview.data?.vormerkungen?.length ?? 0}
        </p>
      </div>}
      {((error && !replacement) || overview.isError || current.isError) && (
        <div role="alert" className="text-red-600">
          {error || 'Lagerdaten konnten nicht geladen werden.'}
          {(overview.isError || current.isError) && <Button size="sm" variant="outline" onClick={() => { void overview.refetch(); void current.refetch() }}>Erneut laden</Button>}
        </div>
      )}
      {message && <p role="status">{message}</p>}
      {intent && (
        <div className="rounded-lg border border-line p-3">
          <p>Ergebnis unbekannt — zuerst prüfen; keine neue Buchung.</p>
          <Button disabled={busy} onClick={() => void recover()}>
            Ergebnis prüfen
          </Button>
        </div>
      )}
      {write && (
        <AuftragAnlegenDialog
          requestKind="pallet"
          company={company}
          enabled={enabled}
          pending={busy || !!intent}
          onPalletRequest={async (code, roll) =>
            !!(await run('request', { code, roll_length_m: roll }))
          }
        />
      )}
      {selected && (
        <section className="rounded-lg border border-line p-3 space-y-2">
          <h3>
            <GrundartikelLink code={selected.merchant_sku} /> ·{' '}
            {selected.status === 'in_transit' ? 'Palette unterwegs' : 'Palette abholen'}
          </h3>
          <p>
            {selected.palette_no} · {selected.roll_length_m ? `R${selected.roll_length_m} · ` : ''}
            {selected.quantity === null
              ? 'Rest unbekannt'
              : `${selected.quantity} ${selected.unit}`}
          </p>
          {selected.device_name && <p>Gerät: {selected.device_name}</p>}
          <p>{palletSourceText(selected.source_lager ?? 2)} · Ziel Lager {selected.target_lager ?? 2}</p>
          <p className="font-semibold">{selected.status === 'ready' ? 'Jetzt: Paletten-QR scannen' : returning || selected.return_required ? `Jetzt: Rückgabeplatz in Lager ${selected.source_lager ?? 2} scannen` : `Jetzt: Zielplatz in Lager ${palletTargetLager(selected)} scannen`}</p>
          <Input
            aria-label={selected.status === 'ready' ? 'Paletten-QR' : 'Platz-QR'}
            placeholder={
              selected.status === 'ready' ? 'Paletten-QR' : `Freier Palettenplatz Lager ${returning ? selected.source_lager ?? 2 : palletTargetLager(selected)}`
            }
            value={qr}
            onChange={(e) => setQr(e.target.value)}
          />
          {write && (
            <Button
              disabled={
                busy ||
                !!intent ||
                !ownsPhysical ||
                !qr.trim() ||
                (selected.status === 'ready' && !enabled)
              }
              onClick={() =>
                void run(
                  selected.status === 'ready' ? 'pickup' : 'place',
                  selected.status === 'ready'
                    ? { palette_no: qr.trim() }
                    : {
                        place_barcode: qr.trim(),
                        purpose: returning || selected.return_required ? 'return' : 'fulfil',
                      },
                )
              }
            >
              {selected.status === 'ready'
                ? 'Palette abholen'
                : returning || selected.return_required
                  ? 'Palette zurückstellen'
                  : 'Palette abstellen'}
            </Button>
          )}
          {write && (selected.status !== 'ready' || selected.can_cancel_ready === true) && (
            <Button
              variant="outline"
              disabled={busy || !!intent || !ownsPhysical}
              onClick={() => void run('cancel')}
            >
              Stornieren / Rückgabe
            </Button>
          )}
          {warehouse.lead && selected.status === 'in_transit' && (
            <><Button variant="outline" onClick={() => { setTransferTarget(selected); setTransferOpen(true) }}>Übertragen</Button><Dialog open={transferOpen} onOpenChange={setTransferOpen}><DialogContent><DialogTitle>Transport übertragen · {transferTarget?.palette_no}</DialogTitle>
              {!transferAvailable && <p role="alert">Dieser Transport ist nicht mehr verfügbar oder konnte nicht geprüft werden.</p>}
              <div className="flex gap-2"><Button variant={transferKind === 'user' ? 'default' : 'outline'} onClick={() => setTransferKind('user')}>Benutzer</Button><Button variant={transferKind === 'scanner' ? 'default' : 'outline'} onClick={() => setTransferKind('scanner')}>Scanner</Button></div>
            {transferKind === 'user' ? <div className="space-y-2">
              <p>Übertragen an Benutzer-ID (Leiter prüft Empfänger)</p>
              <Input
                aria-label="Empfänger Benutzer-ID"
                value={receiver}
                onChange={(e) => setReceiver(e.target.value)}
              />
              <Button
                disabled={busy || !!intent || !transferAvailable || !/^\d+$/.test(receiver)}
                onClick={() =>
                  void run('transfer', {
                    expected_owner: transferTarget!.owner_key,
                    new_owner: { user_id: Number(receiver) },
                  }, transferTarget)
                }
              >
                Übertragen
              </Button>
            </div> : <div className="space-y-2"><p>Scanner und Mitarbeiter</p>
              <Input
                aria-label="Empfänger Geräte-ID"
                value={receiverDevice}
                onChange={(e) => setReceiverDevice(e.target.value)}
              />
              <Input
                aria-label="Empfänger Mitarbeiter-ID"
                value={receiverWorker}
                onChange={(e) => setReceiverWorker(e.target.value)}
              />
              <Button
                disabled={
                  busy ||
                  !!intent ||
                  !transferAvailable ||
                  !/^[1-9]\d*$/.test(receiverDevice) ||
                  !/^[1-9]\d*$/.test(receiverWorker)
                }
                onClick={() =>
                  void run('transfer', {
                    expected_owner: transferTarget!.owner_key,
                    new_owner: {
                      device_client_id: Number(receiverDevice),
                      worker_id: Number(receiverWorker),
                    },
                  }, transferTarget)
                }
              >
                An Scanner übertragen
              </Button>
            </div>}</DialogContent></Dialog></>
          )}
        </section>
      )}
      <section className="space-y-2">
        {rows.length > 0 && <h3>Freie Reihenfolge</h3>}
        <div className="flex gap-2">{([1, 2] as const).map(n => <Button key={n} variant={lager === n ? 'default' : 'outline'} disabled={busy || !!intent} onClick={() => setLager(n)}>Lager {n}</Button>)}</div>
        {overview.isLoading && <Skeleton className="h-20 w-full" />}
        {overview.data && !overview.isError && !current.isError && !current.isLoading && rows.length === 0 && !overview.data.vormerkungen.length && <p>Keine offenen Palettenaufträge.</p>}
        {rows.map((row) => (
          <div key={row.transport_id} className="rounded-lg border border-line bg-panel p-3 flex items-start gap-3">
            <AuftragSymbol art="palette" />
            <div className="flex-1 min-w-0">
              <b><GrundartikelLink code={row.merchant_sku} /></b>
              <div className="text-base font-semibold">{row.place_barcode ?? '—'} · {row.palette_no}</div>
              <span className="rounded-full bg-primary-soft text-primary px-2 py-0.5 text-xs">{row.status === 'ready' ? 'Bereit' : 'Unterwegs'}</span>
              <p>
                1 ganze Palette · {palletSourceText(row.source_lager ?? 2)} · Ziel Lager {row.target_lager ?? 2} · {row.roll_length_m ? `R${row.roll_length_m} · ` : ''}Quelle{' '}
                {row.place_barcode ?? '—'} ·{' '}
                {row.quantity === null ? 'Rest unbekannt' : `${row.quantity} ${row.unit}`}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  disabled={busy || !!intent}
                  onClick={() => {
                    setSelected(row)
                    setQr('')
                    setReturning(false)
                  }}
                >
                  Palette öffnen
                </Button>
                {warehouse.lead && row.status === 'ready' && row.can_cancel_ready === true && (
                  <Button
                    variant="outline"
                    disabled={busy || !!intent || !!replacement}
                    onClick={() => setListConfirm({ kind: 'cancel', row })}
                  >
                    Stornieren
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </section>
      {!!overview.data?.vormerkungen?.length && <section>
        <h3>Wartende Palettenaufträge ({overview.data.vormerkungen.length})</h3>
        {overview.data?.vormerkungen?.map((row) => (
          <div key={row.id} className="flex flex-wrap items-center justify-between gap-2 py-1">
            <p className="flex items-center gap-2">
              <AuftragSymbol art="palette" />
              <span>
                <GrundartikelLink code={row.merchant_sku} /> {row.roll_length_m ? `R${row.roll_length_m}` : ''} · 1 ganze Palette
                · {palletSourceText(row.source_lager)} · Ziel Lager {row.target_lager ?? 2}
              </span>
            </p>
            {warehouse.lead && (
              <Button
                variant="outline"
                size="sm"
                disabled={busy || !!intent}
                onClick={() => setListConfirm({ kind: 'withdraw', row })}
              >
                Zurückziehen
              </Button>
            )}
          </div>
        ))}
      </section>}
    </div>
  )
}
